import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Plus, Key, Trash2, CheckCircle, XCircle, Server, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  nl: {
    title: 'API Sleutels',
    subtitle: 'Gebruik je eigen AI-modellen. Je betaalt credits voor het platform, maar gebruikt je eigen API sleutel voor het AI-model.',
    addKey: 'Sleutel toevoegen',
    noKeys: 'Geen API Sleutels',
    noKeysDesc: 'Voeg je eigen OpenAI, Anthropic, Gemini of Custom LLM sleutel toe',
    addFirst: 'Eerste sleutel toevoegen',
    provider: 'Provider',
    keyName: 'Naam',
    keyNamePlaceholder: 'Bijv. Mijn OpenAI sleutel',
    apiKey: 'API Sleutel',
    apiKeyPlaceholder: 'sk-...',
    baseUrl: 'Server URL (endpoint)',
    baseUrlPlaceholder: 'https://mijn-llm-server.nl/v1',
    baseUrlHint: 'OpenAI-compatible endpoint (bijv. /v1/chat/completions)',
    encrypted: 'Je sleutel wordt versleuteld opgeslagen',
    cancel: 'Annuleren',
    add: 'Toevoegen',
    test: 'Testen',
    testing: 'Bezig...',
    delete: 'Verwijderen',
    deleteConfirm: 'Weet je zeker dat je deze sleutel wilt verwijderen?',
    added: 'Toegevoegd',
    lastUsed: 'Laatst gebruikt',
    never: 'Nooit',
    success: 'Sleutel werkt!',
    invalid: 'Sleutel is ongeldig',
    customHint: 'Voor eigen LLM-servers (overheid, private cloud). Moet OpenAI-compatible API ondersteunen.',
  },
  en: {
    title: 'API Keys',
    subtitle: 'Use your own AI models. You pay credits for the platform, but use your own API key for the AI model.',
    addKey: 'Add Key',
    noKeys: 'No API keys yet — connect one to get started.',
    noKeysDesc: 'Add your own OpenAI, Anthropic, Gemini or Custom LLM key',
    addFirst: 'Add First Key',
    provider: 'Provider',
    keyName: 'Name',
    keyNamePlaceholder: 'E.g. My OpenAI key',
    apiKey: 'API Key',
    apiKeyPlaceholder: 'sk-...',
    baseUrl: 'Server URL (endpoint)',
    baseUrlPlaceholder: 'https://my-llm-server.com/v1',
    baseUrlHint: 'OpenAI-compatible endpoint (e.g. /v1/chat/completions)',
    encrypted: 'Your key is encrypted and stored securely',
    cancel: 'Cancel',
    add: 'Add',
    test: 'Test',
    testing: 'Testing...',
    delete: 'Delete',
    deleteConfirm: 'Are you sure you want to delete this key?',
    added: 'Added',
    lastUsed: 'Last used',
    never: 'Never',
    success: 'Key is working!',
    invalid: 'Key is invalid',
    customHint: 'For self-hosted LLM servers (government, private cloud). Must support OpenAI-compatible API.',
  },
};

const PROVIDERS = [
  { id: 'openai', label: 'OpenAI', placeholder: 'sk-...' },
  { id: 'anthropic', label: 'Anthropic (Claude)', placeholder: 'sk-ant-...' },
  { id: 'gemini', label: 'Google Gemini', placeholder: 'AIza...' },
  { id: 'custom', label: 'Custom LLM', placeholder: 'Optioneel' },
];

const APIKeys = () => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = T[lang] || T.nl;
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [newKey, setNewKey] = useState({ provider: 'openai', key_name: '', api_key: '', base_url: '' });

  useEffect(() => { fetchKeys(); }, []);

  const fetchKeys = async () => {
    try {
      const res = await axios.get(`${API}/api-keys/list`, { headers: { Authorization: `Bearer ${token}` } });
      setKeys(res.data.api_keys || []);
    } catch (_) {}
    setLoading(false);
  };

  const addKey = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newKey };
      if (payload.provider !== 'custom') delete payload.base_url;
      await axios.post(`${API}/api-keys/add`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setShowModal(false);
      setNewKey({ provider: 'openai', key_name: '', api_key: '', base_url: '' });
      fetchKeys();
    } catch (_) {}
  };

  const deleteKey = async (keyId) => {
    if (!window.confirm(t.deleteConfirm)) return;
    try {
      await axios.delete(`${API}/api-keys/${keyId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchKeys();
    } catch (_) {}
  };

  const testKey = async (keyId) => {
    setTestingId(keyId);
    try {
      const res = await axios.post(`${API}/api-keys/${keyId}/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      alert(res.data.status === 'valid' ? t.success : t.invalid + ': ' + res.data.message);
    } catch (_) { alert(t.invalid); }
    setTestingId(null);
  };

  const selectedProvider = (Array.isArray(PROVIDERS) ? PROVIDERS : []).find(p => p.id === newKey.provider);

  if (loading) return <div className="loading-screen"><div className="loader" /><p>Loading...</p></div>;

  return (
    <div data-testid="api-keys-page" style={{ maxWidth: 800, margin: '0 auto', padding: '2rem 1.5rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 className="heading-1" style={{ fontSize: '1.6rem' }}>{t.title}</h1>
          <button onClick={() => setShowModal(true)} className="btn-primary" data-testid="add-api-key-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0.5rem 1rem', fontSize: '0.85rem' }}>
            <Plus size={16} /> {t.addKey}
          </button>
        </div>
      </div>
      <p className="body-medium" style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem' }}>{t.subtitle}</p>

      {/* Keys list */}
      {keys.length === 0 ? (
        <div data-testid="api-keys-empty" style={{
          textAlign: 'center', padding: '3rem 2rem', background: '#16202C',
          borderRadius: 12, border: '1px dashed #ddd',
        }}>
          <Key size={40} style={{ color: '#bbb', marginBottom: '1rem' }} />
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.4rem' }}>{t.noKeys}</h3>
          <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem' }}>{t.noKeysDesc}</p>
          <button onClick={() => setShowModal(true)} className="btn-primary" data-testid="add-first-key-btn"
            style={{ padding: '0.5rem 1.2rem', fontSize: '0.85rem' }}>
            {t.addFirst}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {(Array.isArray(keys) ? keys : []).map((key) => (
            <div key={key.id} data-testid={`api-key-${key.id}`} style={{
              background: '#0E1620', border: '1px solid #e5e5e5', borderRadius: 10,
              padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem',
            }}>
              <div style={{
                background: key.provider === 'custom' ? '#16202C' : '#16202C',
                borderRadius: 8, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {key.provider === 'custom' ? <Server size={18} style={{ color: '#16a34a' }} /> : <Key size={18} style={{ color: '#4f46e5' }} />}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{key.key_name}</span>
                  <span style={{
                    background: '#16202C', padding: '1px 8px', borderRadius: 4,
                    fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: '#666',
                  }}>{key.provider}</span>
                </div>
                <div style={{ display: 'flex', gap: '1rem', marginTop: 4, fontSize: '0.78rem', color: '#999' }}>
                  <span style={{ fontFamily: 'monospace' }}>{key.key_hint}</span>
                  <span>{t.added}: {new Date(key.created_at).toLocaleDateString()}</span>
                  {key.last_used && <span>{t.lastUsed}: {new Date(key.last_used).toLocaleDateString()}</span>}
                </div>
              </div>
              <button onClick={() => testKey(key.id)} disabled={testingId === key.id}
                data-testid={`test-key-${key.id}`}
                style={{
                  background: 'none', border: '1px solid #ddd', borderRadius: 6,
                  padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', color: '#555',
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                {testingId === key.id ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle size={14} />}
                {testingId === key.id ? t.testing : t.test}
              </button>
              <button onClick={() => deleteKey(key.id)} data-testid={`delete-key-${key.id}`}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626',
                  padding: 6, borderRadius: 6,
                }}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Key Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setShowModal(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#0E1620', borderRadius: 14, padding: '1.75rem', maxWidth: 460, width: '92vw',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '1.25rem' }}>{t.addKey}</h2>
            <form onSubmit={addKey} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Provider */}
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#F4F7FB', marginBottom: 4, display: 'block' }}>{t.provider}</label>
                <select value={newKey.provider} data-testid="provider-select"
                  onChange={(e) => setNewKey({ ...newKey, provider: e.target.value })}
                  style={{
                    width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8,
                    border: '1px solid #ddd', fontSize: '0.9rem', background: '#0E1620',
                  }}>
                  {(Array.isArray(PROVIDERS) ? PROVIDERS : []).map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>

              {/* Custom LLM hint */}
              {newKey.provider === 'custom' && (
                <div style={{
                  background: '#16202C', border: '1px solid #bbf7d0', borderRadius: 8,
                  padding: '0.65rem 0.85rem', fontSize: '0.8rem', color: '#15803d',
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}>
                  <Server size={16} style={{ marginTop: 1, flexShrink: 0 }} />
                  {t.customHint}
                </div>
              )}

              {/* Key Name */}
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#F4F7FB', marginBottom: 4, display: 'block' }}>{t.keyName}</label>
                <input type="text" required value={newKey.key_name} data-testid="key-name-input"
                  onChange={(e) => setNewKey({ ...newKey, key_name: e.target.value })}
                  placeholder={t.keyNamePlaceholder}
                  style={{
                    width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8,
                    border: '1px solid #ddd', fontSize: '0.9rem',
                  }} />
              </div>

              {/* Base URL for Custom LLM */}
              {newKey.provider === 'custom' && (
                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#F4F7FB', marginBottom: 4, display: 'block' }}>{t.baseUrl}</label>
                  <input type="url" required value={newKey.base_url} data-testid="base-url-input"
                    onChange={(e) => setNewKey({ ...newKey, base_url: e.target.value })}
                    placeholder={t.baseUrlPlaceholder}
                    style={{
                      width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8,
                      border: '1px solid #ddd', fontSize: '0.9rem', fontFamily: 'monospace',
                    }} />
                  <p style={{ fontSize: '0.75rem', color: '#888', marginTop: 4 }}>{t.baseUrlHint}</p>
                </div>
              )}

              {/* API Key */}
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#F4F7FB', marginBottom: 4, display: 'block' }}>{t.apiKey}</label>
                <input type="password" value={newKey.api_key} data-testid="api-key-input"
                  required={newKey.provider !== 'custom'}
                  onChange={(e) => setNewKey({ ...newKey, api_key: e.target.value })}
                  placeholder={selectedProvider?.placeholder || 'sk-...'}
                  style={{
                    width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8,
                    border: '1px solid #ddd', fontSize: '0.9rem', fontFamily: 'monospace',
                  }} />
                <p style={{ fontSize: '0.75rem', color: '#888', marginTop: 4 }}>{t.encrypted}</p>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button type="button" onClick={() => setShowModal(false)}
                  style={{
                    padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #ddd',
                    background: '#0E1620', fontSize: '0.85rem', cursor: 'pointer',
                  }}>{t.cancel}</button>
                <button type="submit" className="btn-primary" data-testid="submit-key-btn"
                  style={{ padding: '0.5rem 1.2rem', fontSize: '0.85rem' }}>{t.add}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default APIKeys;
